/**
 * Reports → Getting a break (Reports step 4, DESIGN-reports.md; Rob's decisions 12/10/2026).
 *
 * Who hasn't had time off lately — the old "burnout" card, rebuilt (decision M). As of today,
 * longest without a break first, flagged after the days set on Settings → Leave (90 by
 * default, Rob's 5a). Anyone with Reports may open it (2a); the server cuts it to their reach.
 *
 * A break = at least one full day of booked leave that isn't sickness. Weekends, bank
 * holidays and working from home don't count.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { reportsApi, apiErrorMessage, saveBlobResponse } from '../../lib/api';
import { formatDateGB } from '../../lib/dates';
import { breakChips, daysWord, lastBreakText } from '../../lib/sicknessWords';
import {
  ReportFrame, Card, Chip, PillSelect, DownloadMenu, PrintButton, NoAccess, InfoTip, fmtDays, HEX,
  ScrollTable,
} from '../../components/reports/ReportUi';

const PAGE_DOWNLOADS = [
  { value: 'xlsx', label: 'Excel' },
  { value: 'csv', label: 'CSV' },
];

const Stat = ({ label, value, sub, navy = false, active, onClick, testid }) => (
  <div role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined} onClick={onClick}
       data-testid={testid} className={onClick ? 'cursor-pointer' : ''}>
    <Card className={`h-full transition-shadow ${onClick ? 'hover:shadow-[0_10px_30px_rgba(19,31,60,.10)]' : ''} ${
      navy ? 'relative overflow-hidden !bg-hive-navy !border-hive-navy text-white dark:!bg-hive-navy' : ''} ${
      active ? 'ring-2 ring-hive-orange' : ''}`}>
      {navy && <div className="absolute -right-5 -top-4 w-24 h-24 bg-hive-orange opacity-[.12]" style={{ clipPath: HEX }} />}
      <p className={`text-[12.5px] font-bold ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>{label}</p>
      <p className="font-poppins text-[28px] font-bold leading-tight">{value}</p>
      {sub && <p className={`text-xs ${navy ? 'text-hive-muted-cool' : 'text-hive-muted dark:text-muted-foreground'}`}>{sub}</p>}
    </Card>
  </div>
);

const STATUS = { taken: 'Taken', booked: 'Booked', waiting: 'Waiting for approval' };

const GettingABreakPage = () => {
  const [filters, setFilters] = useState({ department_id: '', location_id: '' });
  const [show, setShow] = useState('all');        // all | over | none
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [openPerson, setOpenPerson] = useState(null);
  const [personData, setPersonData] = useState(null);
  const [busy, setBusy] = useState(null);

  const params = useMemo(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportsApi.breaks(params);
      setData(res.data);
      setForbidden(false);
    } catch (err) {
      if (err?.response?.status === 403) setForbidden(true);
      else toast.error(apiErrorMessage(err, 'Could not load the report'));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOpenPerson(null); }, [params]);

  useEffect(() => {
    if (!openPerson) return undefined;
    let cancelled = false;
    setPersonData(null);
    (async () => {
      try {
        const res = await reportsApi.breaksPerson(openPerson);
        if (!cancelled) setPersonData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(apiErrorMessage(err, 'Could not load their time off'));
      }
    })();
    return () => { cancelled = true; };
  }, [openPerson]);

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await reportsApi.exportBreaks({ ...params, format: fmt });
      const name = saveBlobResponse(res, `getting-a-break.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`);
      toast.success(`Downloaded ${name}`);
    } catch (err) {
      let message = 'Could not build the download';
      try {
        const text = await err?.response?.data?.text?.();
        if (text) message = JSON.parse(text).detail || message;
      } catch { /* keep the fallback */ }
      toast.error(message);
    } finally {
      setBusy(null);
    }
  };

  if (forbidden) return <ReportFrame><NoAccess /></ReportFrame>;

  const h = data?.headline;
  const limit = data?.threshold ?? 90;
  const yearStart = data ? formatDateGB(data.leave_year.start) : '';
  const rows = (data?.people || []).filter((r) => (show === 'over' ? r.over : show === 'none' ? r.none_on_record : true));
  const toggle = (k) => setShow((s) => (s === k ? 'all' : k));

  return (
    <ReportFrame>
      <p className="text-[13px] text-hive-muted dark:text-muted-foreground mb-1 print:hidden">
        <Link to="/reports" className="hover:underline">Reports</Link> › <b className="text-hive-navy dark:text-foreground">Getting a break</b>
      </p>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-poppins text-[26px] font-semibold">Getting a break</h1>
          <p className="text-sm text-hive-muted dark:text-muted-foreground">
            {data ? <>{data.scope.label} · as of today, {formatDateGB(data.as_of)} · flags anyone without a break for {limit} days (<Link to="/settings?tab=leave" className="hover:underline">Settings → Leave</Link>)</> : 'Loading…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PrintButton />
          <DownloadMenu options={PAGE_DOWNLOADS} busy={!!busy} onPick={download} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3 print:hidden">
        <PillSelect label="Department" value={filters.department_id} onChange={(v) => setFilters((f) => ({ ...f, department_id: v }))}>
          <option value="">All departments</option>
          {(data?.filters.departments || []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </PillSelect>
        <PillSelect label="Location" value={filters.location_id} onChange={(v) => setFilters((f) => ({ ...f, location_id: v }))}>
          <option value="">All locations</option>
          {(data?.filters.locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </PillSelect>
      </div>

      <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3.5 my-4 ${loading ? 'opacity-60' : ''}`}>
        <Stat navy testid="break-stat-over" active={show === 'over'} onClick={() => toggle('over')}
              label={`No break in ${limit} days or more`}
              value={h ? `${h.over} ${h.over === 1 ? 'person' : 'people'}` : '–'}
              sub={h ? `of ${h.people} · ${show === 'over' ? 'click again to show everyone' : 'click to show only them'}` : ''} />
        <Stat label="…with nothing booked" value={h ? h.over_nothing_booked : '–'}
              sub={!h ? '' : !h.over ? 'nobody is over the limit'
                : h.over_nothing_booked === h.over ? 'none of them has a holiday coming'
                  : `${h.over - h.over_nothing_booked} ${h.over - h.over_nothing_booked === 1 ? 'has' : 'have'} a holiday coming`} />
        <Stat label="Longest without a break" value={h?.longest ? daysWord(h.longest.days) : '–'}
              sub={h?.longest ? `${h.longest.name}, ${h.longest.none_on_record ? 'nothing on record since' : 'since'} ${formatDateGB(h.longest.since)}` : ''} />
        <Stat testid="break-stat-none" active={show === 'none'} onClick={() => toggle('none')}
              label="No holiday on record" value={h ? `${h.none_on_record} ${h.none_on_record === 1 ? 'person' : 'people'}` : '–'}
              sub="counted from when they joined, or were added" />
      </div>

      <Card className={loading ? 'opacity-60' : ''}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-poppins font-semibold text-[17px]">
            {show === 'over' ? `Over ${limit} days, longest first` : show === 'none' ? 'No holiday on record' : 'Longest without a break first'}
          </p>
          <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground">Click a person to see their time off this leave year</p>
        </div>
        <ScrollTable>
          <table className="w-full text-[13.5px] mt-2" data-testid="break-people">
            <thead>
              <tr className="text-[12px] text-hive-muted dark:text-muted-foreground border-b border-hive-line dark:border-border">
                <th className="text-left font-bold py-2 pr-2">Person</th>
                <th className="text-left font-bold px-2">Last break</th>
                <th className="text-right font-bold px-2 whitespace-nowrap">Days since</th>
                <th className="text-left font-bold px-2 whitespace-nowrap">Next booked</th>
                <th className="text-right font-bold px-2 whitespace-nowrap">
                  Longest gap this leave year{' '}
                  <InfoTip label="About the longest gap">
                    The longest stretch between breaks since {yearStart} (or since they joined), counting the gap
                    they're in now. Someone can have had a break recently and still have gone months without one
                    earlier in the year.
                  </InfoTip>
                </th>
                <th className="pl-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const last = lastBreakText(r);
                return (
                  <React.Fragment key={r.user_id}>
                    <tr onClick={() => setOpenPerson((id) => (id === r.user_id ? null : r.user_id))}
                        className={`border-b border-hive-line-soft dark:border-border cursor-pointer align-top hover:bg-hive-tile dark:hover:bg-muted ${
                          openPerson === r.user_id ? 'bg-hive-pending-bg dark:bg-muted' : ''}`}>
                      <td className="py-2 pr-2">
                        <b>{r.name}</b>
                        <span className="block text-[12px] text-hive-muted dark:text-muted-foreground">{r.department || 'No department'}</span>
                      </td>
                      <td className="py-2 px-2">
                        {last.main}
                        {last.sub && <span className="block text-[12px] text-hive-muted dark:text-muted-foreground">{last.sub}</span>}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums">{r.on_break ? '–' : fmtDays(r.days_since)}</td>
                      <td className="py-2 px-2 whitespace-nowrap">
                        {r.next_booked ? formatDateGB(r.next_booked) : <span className="text-hive-muted text-[12.5px]">Nothing booked</span>}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums whitespace-nowrap">{daysWord(r.longest_gap)}</td>
                      <td className="py-2 pl-2">
                        {breakChips(r, limit).map((c) => <React.Fragment key={c.text}><Chip tone={c.tone}>{c.text}</Chip>{' '}</React.Fragment>)}
                      </td>
                    </tr>
                    {openPerson === r.user_id && (
                      <tr><td colSpan={6} className="pb-3 pt-1">
                        <div className="rounded-2xl border border-hive-line dark:border-border bg-hive-tile dark:bg-card px-4 py-3" data-testid="break-person-drill">
                          <p className="font-bold">{r.name} · time off since {yearStart}</p>
                          {!personData ? <p className="text-sm text-hive-muted mt-2">Loading…</p> : (
                            <table className="w-full text-[13px] mt-2">
                              <thead>
                                <tr className="text-[12px] text-hive-muted border-b border-hive-line dark:border-border">
                                  <th className="text-left font-bold py-1.5">Dates</th>
                                  <th className="text-left font-bold">Leave type</th>
                                  <th className="text-left font-bold">Status</th>
                                  <th className="text-right font-bold">Days</th>
                                </tr>
                              </thead>
                              <tbody>
                                {personData.bookings.map((b) => (
                                  <tr key={`${b.from}-${b.to}-${b.leave_type}`} className="border-b border-hive-line-soft dark:border-border align-top">
                                    <td className="py-1.5">
                                      {formatDateGB(b.from)}{b.to !== b.from ? ` – ${formatDateGB(b.to)}` : ''}
                                      {!b.counts && <span className="block text-[11.5px] text-hive-muted">less than a full day, so not a break</span>}
                                    </td>
                                    <td>{b.leave_type}</td>
                                    <td><Chip tone={b.status === 'taken' ? 'grey' : b.status === 'waiting' ? 'pending' : 'success'}>{STATUS[b.status]}</Chip></td>
                                    <td className="text-right tabular-nums">{fmtDays(b.days)}</td>
                                  </tr>
                                ))}
                                {!personData.bookings.length && (
                                  <tr><td colSpan={4} className="py-3 text-hive-muted">No time off this leave year, and nothing booked.</td></tr>
                                )}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!rows.length && (
                <tr><td colSpan={6} className="py-6 text-center text-hive-muted">Nobody here.</td></tr>
              )}
            </tbody>
          </table>
        </ScrollTable>
        <p className="text-[12.5px] text-hive-muted dark:text-muted-foreground mt-3">
          <b>A break</b> is at least one full day of booked leave that isn't sickness. Weekends, bank holidays and working
          from home don't count, because everyone gets those. "Next booked" includes holidays still waiting for approval.
          Someone with nothing on record is counted from the day they joined, or were added to HolidayHive if that's later.
          "Nearly" means within {data?.nearly_days ?? 14} days of the limit.
        </p>
      </Card>
    </ReportFrame>
  );
};

export default GettingABreakPage;
